<script setup>
import BreadCrumbs from '@/components/breadcrumbs/BreadCrumbs.vue'
import ContentSubheaderPage from '@/components/content/ContentSubheaderPage.vue'
import AllPartnersPageCard from '@/components/partners/AllPartnersPageCard.vue'
import { usePartnersStore } from '@/store/partners'
import { onBeforeUpdate, onMounted, ref, watch } from 'vue'
import * as helper from '@/store/helpers'

const partnersStore = usePartnersStore()

const partnersCount = ref(0)
const partners = ref([])

const pageNumber = ref(1) // Текущая страница
const paginationCount = ref(4) // Количество новостей на 1 странице
const maxPage = ref(0)
const pageElems = ref([]) // ref-ссылки на dom-элементы (номера страниц)

const getMaxPage = (partnersCount) => {
    return Math.ceil(partnersCount / paginationCount.value)
}

const changePage = (page) => {
    pageNumber.value = page

    for (let i = 0; i < pageElems.value.length; i++) {
        if (pageElems.value[i].classList.contains('toggle')) {
            pageElems.value[i].classList.remove('toggle')
            break
        }
    }

    if (pageElems.value.length > 1) {
        pageElems.value[page - 1].classList.add('toggle')
    }

    document
        .getElementById('all-partners-page')
        .scrollIntoView({ behavior: 'smooth' }) // Прокрутка страницы
}

const getPartners = async (isFirstPage = true) => {
    if (isFirstPage) {
        partnersCount.value = await helper.api('GET', '/partners/count', {})
        await partnersStore.getPartners(
            0,
            paginationCount.value * pageNumber.value
        )
        partners.value = partnersStore.allPartners
    } else {
        partnersCount.value = await helper.api('GET', '/partners/count', {})
        await partnersStore.getPartners(
            paginationCount.value * (pageNumber.value - 1),
            paginationCount.value * pageNumber.value
        )
        partners.value = partnersStore.allPartners
    }
}

watch(pageNumber, async () => {
    await getPartners(false)
})

onMounted(async () => {
    await getPartners()
    maxPage.value = getMaxPage(partnersCount.value)
})

onBeforeUpdate(() => {
    pageElems.value = []
})
</script>

<template>
    <div
        id="all-partners-page"
        class="container"
    >
        <bread-crumbs />
        <content-subheader-page
            class="all-partners-page-content-subheader"
            text="ПАРТНЕРЫ"
        />
        <v-container
            :fluid="true"
            class="pa-0 all-partners-container"
        >
            <v-row class="ml-n2 mr-n2">
                <v-col
                    v-for="partner in partners"
                    :key="partner.id"
                    class="pl-2 pr-2 ml-auto"
                    cols="12"
                >
                    <AllPartnersPageCard v-bind="partner" />
                </v-col>
            </v-row>
        </v-container>
        <v-container
            :fluid="true"
            class="pa-0 pagination"
            :class="{ 'd-none': maxPage < 2 }"
        >
            <div
                class="page-arrows"
                @click="changePage(1)"
            >
                <svg
                    width="20"
                    height="15"
                    viewBox="0 0 20 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M10 1.75152L8.48329 0.5L0 7.5L8.48329 14.5L10 13.2485L3.03342 7.5L10 1.75152Z"
                        fill="#0076DE"
                    />
                    <path
                        d="M20 1.75152L18.4833 0.5L10 7.5L18.4833 14.5L20 13.2485L13.0334 7.5L20 1.75152Z"
                        fill="#0076DE"
                    />
                </svg>
            </div>
            <div
                v-for="(page, i) in maxPage"
                v-show="
                    (pageNumber < 5 ? page <= 5 : page < pageNumber + 3) &&
                    (pageNumber < 5 ? true : page > pageNumber - 3)
                "
                :key="page"
                :ref="
                    (el) => {
                        if (el) pageElems[i] = el
                    }
                "
                class="page"
                :class="{ toggle: i === 0 }"
                @click="changePage(page)"
            >
                {{ page }}
            </div>
            <div
                class="page-arrows"
                @click="changePage(maxPage)"
            >
                <svg
                    width="20"
                    height="15"
                    viewBox="0 0 20 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M10 13.2485L11.5167 14.5L20 7.5L11.5167 0.500001L10 1.75152L16.9666 7.5L10 13.2485Z"
                        fill="#0076DE"
                    />
                    <path
                        d="M0 13.2485L1.51671 14.5L10 7.5L1.51671 0.5L0 1.75152L6.96658 7.5L0 13.2485Z"
                        fill="#0076DE"
                    />
                </svg>
            </div>
        </v-container>
    </div>
</template>

<style lang="scss" scoped>
.container {
    margin-top: 32px;
    margin-left: 104px;
    margin-right: 104px;

    @media screen and (max-width: 767px) {
        margin-left: 40px;
        margin-right: 40px;
        margin-top: 24px;
    }
}

.all-partners-page-content-subheader {
    text-align: left;
}

.all-partners-container {
    margin-top: 28px;
}

.pagination {
    display: flex;
    gap: 32px;
    align-items: center;
    justify-content: center;
    margin-top: 28px;

    .page-arrows {
        cursor: pointer;
    }

    .page {
        color: #0076de;
        font-size: 20px;
        line-height: 28px;
        cursor: pointer;

        &:hover {
            text-decoration: underline;
        }
    }

    .toggle {
        text-decoration: underline;
    }
}
</style>
